import { ICorporationResponse, ICurrentUserResponse } from '@kcftech/prometheus-client';
import { ThemedImage } from 'assets/icon/KCFLogo';
import TermsOfUse from 'assets/TermsOfUse.pdf';
import { fetchAuthSession } from 'auth-provider';
import { Button } from 'components/button/Button';
import { useAccess } from 'contexts/AccessContext';
import { useNotificationContext } from 'contexts/NotificationContext';
import { useRouteEntities } from 'contexts/RouteEntityContext';
import { sdClientMetadata } from 'contexts/SDKClientContext';
import { useUser } from 'contexts/UserContext';
import { getKCFAcademyUrl } from 'pages/Location/shared';
import {
    browserName,
    deviceType,
    fullBrowserVersion,
    osName,
    osVersion,
} from 'react-device-detect';
import { ExternalLink } from 'routing/ExternalLink';
import styled from 'styled-components';
import { tokens } from 'styles/tokens';
import { AppInfo } from 'utils/AppInfo';
import { copyToClipboard } from 'utils/clipboard';

const CopyrightContainer = styled.div`
    display: flex;
    flex-direction: column;
    margin-left: 0.8rem;
    padding-left: 0.8rem;
    border-left: 1px solid ${tokens.border.default};
`;

const MenuButton = styled(Button)`
    color: ${tokens.text.link};
    &:hover,
    &:focus {
        background-color: ${tokens.action.hover};
    }
    &:active {
        background-color: ${tokens.action.selected};
    }
`;

const copySupportInfoToClipboard = async (
    user: ICurrentUserResponse | undefined,
    corporation: ICorporationResponse | undefined,
    callback: { onSuccess: () => void; onError: () => void },
) => {
    const supportInfo = [
        `URL: ${window.location.href}`,
        `Version: ${
            AppInfo.release
                ? `${AppInfo.version}+${AppInfo.build}`
                : `${AppInfo.version}-prerelease`
        }`,
        `API Version: ${sdClientMetadata.PrometheusApiVersion ?? 'unknown'}`,
        `Browser: ${browserName} ${fullBrowserVersion}`,
        `OS: ${osName} ${osVersion}`,
        `Device Type: ${deviceType}`,
        `Resolution: ${window.innerWidth}x${window.innerHeight} [DPI: ${window.devicePixelRatio}]`,
        `User: ${user?.FirstName} ${user?.LastName} <${user?.Email}> [Role: ${user?.ApplicationRole}]`,
        `Language: ${navigator.language}`,
        'User Preferences:',
        `\tDate Format: ${user?.Profile.DateFormat}`,
        `\tTime Format: ${user?.Profile.TimeFormat}`,
        `\tTime Zone: ${user?.Profile.TimeZones}`,
        `\tAcceleration Units: ${user?.Profile.AccelerationUnits}`,
        `\tVelocity Units: ${user?.Profile.VelocityUnits}`,
        `\tTemperature Units: ${user?.Profile.TemperatureUnits}`,
        `\tPressure Units: ${user?.Profile.PressureUnits}`,
        corporation !== undefined
            ? `Access Level for current Corporation: ${corporation.AccessLevel.toString()}`
            : undefined,
    ].join('\n');

    try {
        await copyToClipboard(supportInfo);
        callback.onSuccess();
    } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
        callback.onError();
    }
};

export function HelpMenu(): JSX.Element {
    const { corporation } = useRouteEntities();
    const user = useUser();

    const { showToast } = useNotificationContext();

    const onCopyAuthClick = async () => {
        try {
            const session = await fetchAuthSession();
            const jwt = session.tokens?.accessToken.toString();
            if (!jwt) {
                showToast({ type: 'error', content: 'Failed to copy auth token' });
                return;
            }
            copyToClipboard(jwt);
            showToast({ type: 'success', content: 'Auth token copied' });
        } catch {
            showToast({ type: 'error', content: 'Failed to copy auth token' });
        }
    };

    const { isPremium } = useAccess();

    const version = `${AppInfo.displayVersion()} (API: ${
        sdClientMetadata.PrometheusApiVersion ?? 'unknown'
    })`;

    return (
        <div className="flex-column align-start mx-15 mb-12 fine-print">
            <span className="flex-row full-width">
                <span data-cy="help-menu-version-number">Version: </span>
                <MenuButton
                    data-cy="help-menu-build-number"
                    title="Copy build & debug information to clipboard"
                    onClick={() => {
                        copySupportInfoToClipboard(user.user, corporation, {
                            onSuccess: () =>
                                showToast({
                                    type: 'success',
                                    content: 'Debug information copied to clipboard.',
                                }),
                            onError: () =>
                                showToast({
                                    type: 'error',
                                    content: 'Error copying debug information.',
                                }),
                        });
                    }}
                >
                    {version}
                </MenuButton>
            </span>
            <span className="flex-row full-width">
                {corporation !== undefined && (
                    <span>Access Level: {isPremium ? 'Premium' : 'Basic'}</span>
                )}
            </span>
            <div className="flex-row mb-12">
                <ExternalLink
                    data-cy="help-menu-status-link"
                    url="https://kcfstatus.com"
                    className="mr-5"
                >
                    Status Page
                </ExternalLink>
                |
                <ExternalLink
                    data-cy="help-menu-support-link"
                    url="https://support.kcftech.com"
                    className="mx-5"
                >
                    Help Center
                </ExternalLink>
                |
                <ExternalLink
                    data-cy="kcf-academy-link"
                    url={getKCFAcademyUrl()}
                    className="ml-5"
                >
                    KCF Academy
                </ExternalLink>
            </div>
            <div className="flex-row align-center">
                <ThemedImage
                    icon={'kcf-logo'}
                    alt="KCF Technologies"
                    style={{ height: '2.4rem', width: '5.8rem' }}
                />
                <CopyrightContainer>
                    <span aria-label="Copyright">
                        <span className="text-nowrap">Copyright &copy;</span>
                        <ExternalLink
                            data-cy="help-menu-website-link"
                            url="https://kcftech.com/"
                            className="mx-3 text-nowrap"
                        >
                            KCF Technologies, Inc.
                        </ExternalLink>
                    </span>
                    <span aria-label="Year">
                        {new Date().getFullYear()} All Rights Reserved
                    </span>
                    <span>
                        <ExternalLink
                            data-cy="help-menu-terms-of-use-link"
                            url={TermsOfUse}
                        >
                            Terms of Use
                        </ExternalLink>
                    </span>
                </CopyrightContainer>
            </div>

            <div className="pt-10">
                <MenuButton className="px-0" onClick={onCopyAuthClick}>
                    Copy Auth Token
                </MenuButton>
            </div>
        </div>
    );
}
